import { defineChatActions } from './actionsContext'
import { checkAuth, shouldSuppressAutoReadReceipt } from './runtime'
import { isChatBeingViewed } from '@/modules/chat-matrix-fast/utils/chatSurface'
import {
  compareMessagesAscending,
  findLocalMessage,
  indexLocalMessagesByIdentity,
  isPendingMatrixEventId,
  mergeMessageAttachmentsFromLocal,
  mergeReplyMetadataFromLocal,
  mergeTimelineMessages,
} from './messageMerge'
import { findMessage, findMessageIndex } from '@/modules/chat-matrix-fast/utils/messageIdentity'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { logger } from '@/utils/eventLogger'
import type {
  IMessage,
} from '@/modules/chat-matrix-fast/types/IChat'
import {
  countMatrixRoomUnread,
  countMatrixRoomUnreadRaw,
  forgetLocallyReadTopicRoom,
  isTopicRoomLocallyRead,
  isTopicsModeChat,
  rememberLocallyReadTopicRoom,
  resolveActiveMessageRoomId,
} from '@/modules/chat-matrix-fast/utils/topics'
import {
  ensureMatrixSyncAlive,
  fetchChatFromMatrix,
  fetchMatrixMessages,
  paginateMatrixRoomBackwards,
  fetchMatrixMessagesAroundEvent,
  getCurrentMatrixUserId,
  resolveMatrixRoomIdByChatId,
  sendMatrixForwardedMessage,
  sendMatrixReadReceiptForRoom,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import {
  deleteMatrixMessage,
  editMatrixMessage,
  sendMatrixMessage,
  sendMatrixMessagesWithMedia,
} from '@/modules/chat-matrix-fast/services/matrixMessageSender'
import { getMatrixClient, startMatrixClient } from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { findRoomEventForClientTxn } from '@/modules/chat-matrix-fast/services/matrixTextDelivery'
import {
  looksLikeMatrixEventId,
  markMessageAsDeleted,
} from '@/modules/chat-matrix-fast/services/matrixEventMapper'
import { preserveGroupChatMetadata } from '@/modules/chat-matrix-fast/services/matrixRoomMapper'
import {
  ensureMatrixChatMembership,
  isMatrixMembershipForbiddenError,
} from '@/modules/chat-matrix-fast/services/ensureMatrixChatMembership'
import { ChatApiService } from '@/modules/chat-matrix-fast/services/chatApiService'
import { enrichGroupInviteUrlsInText } from '@/modules/chat-matrix-fast/utils/groupInviteLink'
import {
  buildOutboxItem,
  createOutboxId,
  listOutboxForChat,
  outboxItemToMessage,
  putOutboxItem,
} from '@/modules/chat-matrix-fast/services/messageOutbox'
import {
  flushMatrixMessageOutbox,
} from '@/modules/chat-matrix-fast/services/messageOutboxFlush'
import {
  outboxItemsForUiHydrate,
  removeFlushedOutboxBubble,
  shouldDeferOutboxUiToFlush,
} from '@/modules/chat-matrix-fast/services/messageOutboxHydrate'
import {
  applyPendingReactionOverride,
  mergeMessageReactionsFromLocal,
} from '@/modules/chat-matrix-fast/services/matrixReactionService'
import { VALIDATION_CONSTANTS } from '@/modules/chat-matrix-fast/constants/validation'
import { hasLastMessagePreview } from '@/modules/chat-matrix-fast/constants/chat'
import { isAllowedChatUploadFile, isValidFileSize } from '@/modules/chat-matrix-fast/utils/fileUtils'
import { buildEditedMessageContent } from '@/modules/chat-matrix-fast/utils/messageFormatter'
import { getReplyQuoteText } from '@/modules/chat-matrix-fast/utils/replyPreview'
import { registerPendingReplyMetadata, consumePendingReplyMetadata } from '@/modules/chat-matrix-fast/utils/pendingReplyMetadata'
import {
  getMatrixMessageReaders,
  isMatrixReadMarkerAtLastReadable,
  isMatrixReadReceiptConfirmed,
  notifyMatrixReadReceiptError,
  reconcileRoomUnreadCount,
  resetMatrixReadReceiptCache,
  waitForMatrixReadCaughtUp,
} from '@/modules/chat-matrix-fast/services/matrixReadReceiptService'

import {
  notifyViewLastMessage,
  notifyViewPatchReactions,
  syncViewWithCore,
} from '@/modules/chat-matrix-fast/stores/chatStoreSync'
import {
  clearMessages,
  replaceActiveMessages,
} from '@/modules/chat-matrix-fast/stores/core/messageRoomCache'
import { chatDebugLog, debugConsoleError, lastMessageDebugLog, messagesDebugLog, summarizeLastMessageForLog, summarizeTopicForLog, topicsDebugLog } from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import { chatOpenJumpLog } from '@/modules/chat-matrix-fast/utils/chatOpenJumpLog'

function resolveReplyMatrixEventId(
  replyToId?: number | string,
  replyToMessage?: Partial<IMessage> | null,
): string | undefined {
  if (looksLikeMatrixEventId(replyToMessage?.matrix_event_id)) {
    return replyToMessage.matrix_event_id
  }
  if (looksLikeMatrixEventId(replyToId)) {
    return replyToId
  }
  return undefined
}

export const messageActions = defineChatActions({
  applyRealtimeMessages(messages: IMessage[]): void {
    if (!this.currentChat) {
      return
    }

    if (messages.length === 0 && this.messages.length > 0) {
      return
    }

    chatOpenJumpLog('realtime · apply', {
      chatId: this.currentChat.id,
      incoming: messages.length,
      liveCount: this.messages.length,
    })

    const matrixUserId = getCurrentMatrixUserId() ?? ''
    const localById = indexLocalMessagesByIdentity(this.messages)

    const sortedMessages = mergeTimelineMessages(
      this.messages,
      [...this.enrichMessagesWithMatrixReactions(messages, { applyPending: false })]
        .map((message: IMessage) => {
          const localMessage = findLocalMessage(message, localById)
          const pending = this.getPendingReactionOverride(message)
          const matrixMyReaction = (message.reactions || message.message_reactions || [])
            .find(reaction => String(reaction.user || reaction.user_id) === matrixUserId)

          const withPending = applyPendingReactionOverride(message, pending)
          const withPendingReply = (() => {
            const pendingReply = consumePendingReplyMetadata(message.matrix_txn_id)
            if (!pendingReply) {
              return withPending
            }

            return {
              ...withPending,
              ...pendingReply,
            }
          })()
          const merged = mergeMessageAttachmentsFromLocal(
            mergeMessageReactionsFromLocal(
              withPendingReply,
              localMessage,
              matrixUserId,
            ),
            localMessage,
          )
          const mergedWithReply = mergeReplyMetadataFromLocal(merged, localMessage)

          // Pending снимаем только после подтверждения Matrix (не по reaction_updated_at)
          if (pending) {
            const expected = pending.expectedEmoji
            const settled = expected == null
              ? !matrixMyReaction
              : Boolean(
                matrixMyReaction?.matrix_emoji_key
                && matrixMyReaction.matrix_emoji_key.replace(/\uFE0F/g, '').trim()
                  === expected.replace(/\uFE0F/g, '').trim(),
              )

            if (settled) {
              this.clearPendingReactionOverride(message.id, message.matrix_event_id)
            }
          }

          return mergedWithReply
        }),
    )

    const roomId = resolveActiveMessageRoomId(this.$state)
    const visibleMessages = sortedMessages

    replaceActiveMessages(this, visibleMessages, roomId)

    if (visibleMessages.length > 0) {
      const lastMessage = visibleMessages[visibleMessages.length - 1]
      const chatId = this.currentChat.id
      const chatIndex = this.chats.findIndex(c => c.id === chatId)

      if (chatIndex !== -1) {
        const previousLast = this.chats[chatIndex].last_message
        const previousTime = Date.parse(previousLast?.created_at ?? '')
        const nextTime = Date.parse(lastMessage.created_at ?? '')
        const sameEvent = Boolean(
          previousLast?.matrix_event_id
          && lastMessage.matrix_event_id
          && previousLast.matrix_event_id === lastMessage.matrix_event_id,
        )
        // Только реально более новое событие двигает список; гидрация таймлайна при open — нет.
        const isStrictlyNewer = Boolean(
          hasLastMessagePreview(previousLast)
          && !Number.isNaN(previousTime)
          && !Number.isNaN(nextTime)
          && nextTime > previousTime,
        )
        const shouldUpdateLastMessage = !hasLastMessagePreview(previousLast)
          || isStrictlyNewer
          || sameEvent

        if (shouldUpdateLastMessage) {
          const nextLast = lastMessage
          const updatedChats = [...this.chats]
          updatedChats[chatIndex] = {
            ...updatedChats[chatIndex],
            last_message: nextLast,
            last_message_id: lastMessage.id,
            last_matrix_event_id: lastMessage.matrix_event_id,
          }
          this.chats = updatedChats
          this.updateTitleUnreadCount()
          if (isStrictlyNewer && !sameEvent) {
            notifyViewLastMessage(chatId, nextLast)
          }
        }
      }
    }

    const pendingMessageIds = Object.keys(this.pendingReactionOverrides)

    syncViewWithCore()
    pendingMessageIds.forEach(messageId => {
      notifyViewPatchReactions(messageId)
    })

    // Не шлём receipt на каждый Receipt/timeline tick — иначе шторм POST + петля
    if (roomId && countMatrixRoomUnread(roomId) > 0) {
      void this.acknowledgeMatrixReadReceipts(roomId)
    }
  },

  async acknowledgeMatrixReadReceipts(explicitRoomId?: string): Promise<void> {
    if (!isChatBeingViewed()) {
      return
    }

    const roomId = explicitRoomId?.trim() || resolveActiveMessageRoomId(this.$state)
    if (!roomId) {
      topicsDebugLog('core · acknowledgeMatrixReadReceipts · skip (no roomId)', {
        explicitRoomId: explicitRoomId ?? null,
        activeTopic: summarizeTopicForLog(this.activeTopic),
        currentChatId: this.currentChat?.id ?? null,
      })
      return
    }

    const matrixUnreadBefore = countMatrixRoomUnreadRaw(roomId)
    topicsDebugLog('core · acknowledgeMatrixReadReceipts · start', {
      roomId,
      explicitRoomId: explicitRoomId ?? null,
      activeTopic: summarizeTopicForLog(this.activeTopic),
      currentChatId: this.currentChat?.id ?? null,
      matrixUnreadBefore,
    })

    try {
      const matrixClient = await startMatrixClient()
      // Optimistic zero до ACK: иначе sync/resync вернёт бейдж в навбар/title пока ждём homeserver.
      rememberLocallyReadTopicRoom(roomId)
      this.applyLocalReadForRoom(roomId)

      const receiptResult = await sendMatrixReadReceiptForRoom(matrixClient, roomId)

      if (receiptResult.status === 'error') {
        forgetLocallyReadTopicRoom(roomId)
        this.syncMatrixUnreadForAllChats()
        topicsDebugLog('core · acknowledgeMatrixReadReceipts · matrix error', {
          roomId,
          matrixUnreadBefore,
          error: receiptResult.error,
        })
        notifyMatrixReadReceiptError(receiptResult.error, roomId)
        syncViewWithCore()
        return
      }

      if (receiptResult.status !== 'sent' && receiptResult.status !== 'skipped') {
        forgetLocallyReadTopicRoom(roomId)
        this.syncMatrixUnreadForAllChats()
        topicsDebugLog('core · acknowledgeMatrixReadReceipts · skip local read', {
          roomId,
          receiptResult,
        })
        syncViewWithCore()
        return
      }

      const room = matrixClient.getRoom(roomId)
      const userId = matrixClient.getUserId() ?? ''

      if (room && userId) {
        await waitForMatrixReadCaughtUp(
          room,
          userId,
          () => countMatrixRoomUnreadRaw(roomId) === 0,
        )
      }

      let matrixUnreadAfter = countMatrixRoomUnreadRaw(roomId)
      if (matrixUnreadAfter > 0 && room) {
        const reconciled = await reconcileRoomUnreadCount(matrixClient, room)
        topicsDebugLog('core · acknowledgeMatrixReadReceipts · reconcile', {
          roomId,
          reconciled,
        })
        matrixUnreadAfter = countMatrixRoomUnreadRaw(roomId)
      }

      const markerOk = Boolean(room && userId && isMatrixReadMarkerAtLastReadable(room, userId))
      const caughtUp = matrixUnreadAfter === 0 || markerOk

      if (!isMatrixReadReceiptConfirmed(receiptResult, matrixUnreadAfter) && !caughtUp) {
        if (receiptResult.status === 'skipped') {
          resetMatrixReadReceiptCache(roomId)
        }
        forgetLocallyReadTopicRoom(roomId)
        this.syncMatrixUnreadForAllChats()
        topicsDebugLog('core · acknowledgeMatrixReadReceipts · skip local read', {
          roomId,
          receiptResult,
          matrixUnreadAfter,
          markerOk,
        })
        syncViewWithCore()
        return
      }

      if (!caughtUp) {
        forgetLocallyReadTopicRoom(roomId)
        this.syncMatrixUnreadForAllChats()
        topicsDebugLog('core · acknowledgeMatrixReadReceipts · skip local read (waiting matrix)', {
          roomId,
          receiptResult,
          matrixUnreadAfter,
        })
        syncViewWithCore()
        return
      }

      this.applyLocalReadForRoom(roomId)
      // Держим overlay, пока Synapse не обнулит notification_count — иначе resync вернёт бейдж.
      if (countMatrixRoomUnreadRaw(roomId) === 0) {
        forgetLocallyReadTopicRoom(roomId)
      }

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('matrix-read-receipts-updated', {
          detail: { roomId },
        }))
      }

      topicsDebugLog('core · acknowledgeMatrixReadReceipts · done', {
        roomId,
        receiptResult,
        matrixUnreadBefore,
        matrixUnreadAfter,
        forcedLocalRead: isTopicRoomLocallyRead(roomId),
      })
    } catch (error) {
      forgetLocallyReadTopicRoom(roomId)
      this.syncMatrixUnreadForAllChats()
      topicsDebugLog('core · acknowledgeMatrixReadReceipts · error', {
        roomId,
        matrixUnreadBefore,
        error: String(error),
      })
      notifyMatrixReadReceiptError(error, roomId)
      syncViewWithCore()
    }
  },

  async loadOlderMessages(): Promise<{ hasMore: boolean; added: number }> {
    const chatId = this.currentChat?.id
    const roomId = resolveActiveMessageRoomId(this.$state)
    if (!chatId || !roomId) {
      return { hasMore: false, added: 0 }
    }

    const result = await paginateMatrixRoomBackwards(roomId)
    if (
      this.currentChat?.id !== chatId
      || resolveActiveMessageRoomId(this.$state) !== roomId
      || !result.messages.length
    ) {
      return { hasMore: result.hasMore, added: 0 }
    }

    const before = this.messages.length
    const enriched = await this.enrichMessagesWithMatrixReactionsAsync(result.messages, { roomId })
    if (
      this.currentChat?.id !== chatId
      || resolveActiveMessageRoomId(this.$state) !== roomId
    ) {
      return { hasMore: result.hasMore, added: 0 }
    }
    replaceActiveMessages(this, mergeTimelineMessages(this.messages, enriched), roomId)
    syncViewWithCore()

    return { hasMore: result.hasMore, added: this.messages.length - before }
  },

  async refreshCurrentChatMessages(options?: { silent?: boolean }): Promise<void> {
    if (!this.currentChat?.id) {
      return
    }

    await this.fetchMessages(this.currentChat.id, { mergeWithExisting: true })

    if (options?.silent) {
      syncViewWithCore()
    }
  },

  async fetchMessages(
    chatId: number,
    options?: {
      mergeWithExisting?: boolean
      roomId?: string
      membershipEnsuredRoomId?: string
    },
  ): Promise<void> {
    if (!checkAuth()) {
      return
    }

    const resolveLiveExpectedRoomId = (): string | undefined => {
      if (this.currentChat?.id !== chatId) {
        return undefined
      }

      const topicRoomId = this.activeTopic?.matrix_room_id?.trim()
      if (this.activeTopic) {
        return topicRoomId || undefined
      }

      return this.currentChat?.matrix_room_id?.trim() || undefined
    }

    let targetRoomId = options?.roomId?.trim() || resolveLiveExpectedRoomId()
    const logFetch = this.activeTopic || isTopicsModeChat(this.currentChat)
    if (logFetch) {
      topicsDebugLog('core · fetchMessages · start', {
        chatId,
        targetRoomId: targetRoomId ?? null,
        explicitRoomId: options?.roomId ?? null,
        activeTopic: summarizeTopicForLog(this.activeTopic),
        mergeWithExisting: options?.mergeWithExisting ?? false,
      })
    }

    if (!targetRoomId && !this.activeTopic) {
      try {
        const refreshed = await fetchChatFromMatrix(chatId, this.currentChat)
        if (refreshed.matrix_room_id) {
          const merged = this.currentChat?.id === chatId
            ? preserveGroupChatMetadata(this.currentChat, refreshed)
            : refreshed
          this.upsertChatInList(merged)
          if (this.currentChat?.id === chatId) {
            this.currentChat = merged
          }
          targetRoomId = options?.roomId?.trim()
            || resolveLiveExpectedRoomId()
            || merged.matrix_room_id?.trim()
        } else {
          if (logFetch) {
            topicsDebugLog('core · fetchMessages · abort (no matrix_room_id after refresh)')
          }
          if (this.messages.length === 0) {
            clearMessages(this)
            syncViewWithCore()
          }
          return
        }
      } catch {
        if (logFetch) {
          topicsDebugLog('core · fetchMessages · abort (fetchChatFromMatrix failed)')
        }
        if (this.messages.length === 0) {
          clearMessages(this)
          syncViewWithCore()
        }
        return
      }
    }

    if (!targetRoomId) {
      if (logFetch) {
        topicsDebugLog('core · fetchMessages · abort (no targetRoomId)', {
          hasActiveTopic: Boolean(this.activeTopic),
        })
      }
      if (this.messages.length === 0) {
        clearMessages(this)
        syncViewWithCore()
      }
      return
    }

    const membershipChat = this.currentChat?.id === chatId
      ? this.currentChat
      : this.chats.find(item => item.id === chatId)
    const parentRoomId = membershipChat?.matrix_room_id?.trim() || ''
    const isTopicSatelliteRoom = Boolean(
      this.activeTopic?.matrix_room_id?.trim()
        && this.activeTopic.matrix_room_id.trim() === targetRoomId
        && parentRoomId
        && targetRoomId !== parentRoomId,
    )
    const membershipAlreadyEnsured = !isTopicSatelliteRoom
      && Boolean(options?.membershipEnsuredRoomId)
      && options?.membershipEnsuredRoomId === targetRoomId
    if (!membershipAlreadyEnsured) {
      await ensureMatrixChatMembership({
        chatId,
        roomId: targetRoomId,
        chat: membershipChat,
        forceRepair: isTopicSatelliteRoom,
      })
    }

    // options.roomId — снимок комнаты на старте; stale считаем по живому currentChat/activeTopic
    const isStale = () => {
      if (this.currentChat?.id !== chatId) {
        return true
      }
      const liveRoomId = resolveLiveExpectedRoomId()
      return Boolean(liveRoomId) && liveRoomId !== targetRoomId
    }

    const applyFetchedMessages = async (
      list: IMessage[],
      applyOptions?: { asyncReactions?: boolean },
    ): Promise<number> => {
      if (isStale()) {
        if (logFetch) {
          topicsDebugLog('core · fetchMessages · skip apply (stale)', {
            chatId,
            targetRoomId,
            currentChatId: this.currentChat?.id ?? null,
            liveRoomId: resolveLiveExpectedRoomId() ?? null,
          })
        }
        return 0
      }

      const enriched = applyOptions?.asyncReactions
        ? await this.enrichMessagesWithMatrixReactionsAsync(list, { roomId: targetRoomId })
        : this.enrichMessagesWithMatrixReactions(list, { roomId: targetRoomId })

      if (isStale()) {
        if (logFetch) {
          topicsDebugLog('core · fetchMessages · skip apply after enrich (stale)', {
            chatId,
            targetRoomId,
            currentChatId: this.currentChat?.id ?? null,
            liveRoomId: resolveLiveExpectedRoomId() ?? null,
          })
        }
        return 0
      }

      const sortedMessages = options?.mergeWithExisting && this.currentChat?.id === chatId
        ? mergeTimelineMessages(this.messages, enriched)
        : [...enriched].sort(compareMessagesAscending)

      const previousLast = this.messages[this.messages.length - 1]
      const nextLast = sortedMessages[sortedMessages.length - 1]
      const sameTail = Boolean(
        previousLast
        && nextLast
        && (
          (previousLast.matrix_event_id && previousLast.matrix_event_id === nextLast.matrix_event_id)
          || String(previousLast.id) === String(nextLast.id)
        ),
      )
      const sameLength = this.messages.length === sortedMessages.length

      // Догрузка истории/реакций без смены хвоста — не дёргаем UI лишним sync.
      if (sameTail && sameLength && this.messages.length > 0) {
        replaceActiveMessages(this, sortedMessages, targetRoomId)
        return sortedMessages.length
      }

      replaceActiveMessages(this, sortedMessages, targetRoomId)
      syncViewWithCore()
      return sortedMessages.length
    }

    try {
      // 1) Быстрый путь: хвост sync + warm /messages для cold topic rooms (иначе пустой экран).
      const quickList = await fetchMatrixMessages(targetRoomId, {
        loadHistory: false,
        warmColdTimeline: true,
      })
      if (isStale()) {
        if (logFetch) {
          topicsDebugLog('core · fetchMessages · abort (room changed during quick fetch)', {
            targetRoomId,
            expectedRoomId: resolveLiveExpectedRoomId() ?? null,
            currentChatId: this.currentChat?.id ?? null,
            fetchedCount: quickList.length,
          })
        }
        return
      }

      let paintedFromSync = false
      const acknowledgeIfUnread = () => {
        if (shouldSuppressAutoReadReceipt()) {
          return
        }

        if (countMatrixRoomUnread(targetRoomId) > 0) {
          void this.acknowledgeMatrixReadReceipts(targetRoomId)
        }
      }

      if (quickList.length > 0) {
        const storedCount = await applyFetchedMessages(quickList, { asyncReactions: false })
        paintedFromSync = storedCount > 0
        chatOpenJumpLog('fetchMessages · quick', {
          chatId,
          targetRoomId,
          fetched: quickList.length,
          storedCount,
          liveCount: this.messages.length,
        })
        chatDebugLog('fetchMessages · quick paint from sync', {
          chatId,
          targetRoomId,
          storedCount,
        })
      }

      // 2) История / threads / async-реакции — в фоне, если уже есть что показать.
      const loadFullHistory = async () => {
        chatOpenJumpLog('fetchMessages · full start', {
          chatId,
          targetRoomId,
          liveCount: this.messages.length,
        })
        const list = await fetchMatrixMessages(targetRoomId, { loadHistory: true })
        if (isStale()) {
          chatOpenJumpLog('fetchMessages · full stale', { chatId, targetRoomId })
          return
        }

        const storedCount = await applyFetchedMessages(list, { asyncReactions: true })
        if (isStale() || storedCount === 0) {
          chatOpenJumpLog('fetchMessages · full skip', {
            chatId,
            targetRoomId,
            fetched: list.length,
            storedCount,
            stale: isStale(),
          })
          return
        }

        acknowledgeIfUnread()

        chatOpenJumpLog('fetchMessages · full done', {
          chatId,
          targetRoomId,
          fetched: list.length,
          storedCount,
          liveCount: this.messages.length,
        })
        chatDebugLog('fetchMessages · done', {
          chatId,
          targetRoomId,
          storedCount,
        })
        messagesDebugLog('fetchMessages · timeline snapshot', {
          chatId,
          targetRoomId,
          storedCount,
          first: summarizeLastMessageForLog(this.messages[0]),
          last: summarizeLastMessageForLog(this.messages[this.messages.length - 1]),
          coreChatLastMessage: summarizeLastMessageForLog(
            this.chats.find(chat => chat.id === chatId)?.last_message,
          ),
          currentChatLastMessage: summarizeLastMessageForLog(
            this.currentChat?.id === chatId ? this.currentChat.last_message : undefined,
          ),
        })

        if (logFetch) {
          topicsDebugLog('core · fetchMessages · done', {
            chatId,
            targetRoomId,
            fetchedCount: list.length,
            storedCount,
            activeTopic: summarizeTopicForLog(this.activeTopic),
            matrixUnreadAfterReceipt: countMatrixRoomUnread(targetRoomId),
          })
        }
      }

      if (paintedFromSync) {
        void loadFullHistory().catch(error => {
          chatDebugLog('fetchMessages · background history failed', {
            chatId,
            targetRoomId,
            error: error instanceof Error ? error.message : String(error),
          })
        })
        if (!isStale()) {
          acknowledgeIfUnread()
        }
        return
      }

      // Холодная комната (sync пуст) — ждём полную историю.
      await loadFullHistory()
    } catch (error) {
      debugConsoleError(error)
      logger.error('chat_fetchMessages_error', {
        file: 'chatStore',
        function: 'fetchMessages',
        condition: String(error),
      })

      if (!isStale() && this.messages.length === 0) {
        clearMessages(this)
      }
    }
  },

  async loadMessagesAroundEvent(eventId: string): Promise<boolean> {
    if (!looksLikeMatrixEventId(eventId)) {
      return false
    }

    if (this.messages.some(message => message.matrix_event_id === eventId)) {
      return true
    }

    const chatId = this.currentChat?.id
    const roomId = resolveActiveMessageRoomId(this.$state)
    if (!chatId || !roomId) {
      return false
    }

    try {
      const around = await fetchMatrixMessagesAroundEvent(roomId, eventId)
      if (
        this.currentChat?.id !== chatId
        || resolveActiveMessageRoomId(this.$state) !== roomId
        || !around.length
      ) {
        return false
      }

      const sortedMessages = mergeTimelineMessages(this.messages, around)
      replaceActiveMessages(this, sortedMessages, roomId)
      syncViewWithCore()

      return this.messages.some(message => message.matrix_event_id === eventId)
    } catch (error) {
      debugConsoleError(error)
      return false
    }
  },

  /** Room для отправки: state → currentChat → список чатов → Matrix client */
  async resolveRoomIdForSend(): Promise<string> {
    const chat = this.currentChat
    if (!chat) {
      throw new Error('Нет выбранного чата')
    }

    if (chat.transition_status === 'failed') {
      throw new Error('Чат не удалось подготовить. Обновите или повторите позже.')
    }

    if (chat.transition_status === 'provisioning' && !chat.matrix_room_id?.trim()) {
      throw new Error('Чат ещё создаётся, подождите…')
    }

    let roomId = resolveActiveMessageRoomId(this.$state)
        || chat.matrix_room_id?.trim()
        || this.chats.find(item => item.id === chat.id)?.matrix_room_id?.trim()

    if (!roomId) {
      roomId = await resolveMatrixRoomIdByChatId(chat.id)
    }

    if (!roomId) {
      throw new Error('Чат ещё не подключён')
    }

    let joined = await ensureMatrixChatMembership({
      chatId: chat.id,
      roomId,
      chat,
    })
    // Send path: if still not in room — one Django join repair.
    if (!joined) {
      joined = await ensureMatrixChatMembership({
        chatId: chat.id,
        roomId,
        chat,
        forceRepair: true,
      })
    }
    if (!joined) {
      throw new Error('Нет доступа к чату. Подождите или откройте чат ещё раз.')
    }

    if (!chat.matrix_room_id?.trim()) {
      const updatedChat = { ...chat, matrix_room_id: roomId }
      this.currentChat = updatedChat
      this.upsertChatInList(updatedChat)
    }

    return roomId
  },

  async sendMessage(
    content: string,
    replyToId?: number | string,
    replyToMessage?: any,
    mentionUserIds?: string[],
  ): Promise<IMessage> {
    if (!checkAuth()) {
      throw new Error('Пользователь не авторизован')
    }

    if (!this.currentChat) {
      throw new Error('Нет выбранного чата')
    }
    if (this.isSending) {
      throw new Error('Сообщение уже отправляется')
    }
    this.isSending = true
    try {
      let messageContent = content

      if (replyToId && replyToMessage) {
        const quoteText = getReplyQuoteText({
          content: replyToMessage.content || replyToMessage.message,
          message: replyToMessage.message,
          attachments: replyToMessage.attachments,
        })

        const quoteSender = (replyToMessage as any).senderName
            || replyToMessage.created_by?.full_name
            || replyToMessage.created_by?.user_name
            || replyToMessage.author_name
            || replyToMessage.name
            || 'Неизвестный'

        const quote = `-${quoteSender}\n-${quoteText}\n-`
        messageContent = quote + (content ? `\n${content}` : '')
      }

      messageContent = enrichGroupInviteUrlsInText(messageContent, chatId => {
        const source = this.chats.find(item => item.id === chatId)
          || (this.currentChat?.id === chatId ? this.currentChat : null)
        if (!source?.title?.trim()) {
          return null
        }
        return {
          title: source.title,
          type: source.type,
        }
      })

      const replyEventId = resolveReplyMatrixEventId(replyToId, replyToMessage as IMessage | undefined)
      const resolvedReplyId = replyEventId || replyToId

      const chat = this.currentChat
      const roomIdHint = resolveActiveMessageRoomId(this.$state)
        || chat.matrix_room_id?.trim()
        || this.chats.find(item => item.id === chat.id)?.matrix_room_id?.trim()
        || ''
      const matrixUserId = getCurrentMatrixUserId()
      const clientTxnId = createOutboxId()
      if (resolvedReplyId && replyToMessage) {
        registerPendingReplyMetadata(clientTxnId, resolvedReplyId, replyToMessage as IMessage)
      }
      const replySnapshot = resolvedReplyId && replyToMessage
        ? {
            id: resolvedReplyId,
            matrix_event_id: replyEventId,
            content: replyToMessage.content || replyToMessage.message,
            attachments: replyToMessage.attachments,
            senderName: (replyToMessage as any).senderName
              || replyToMessage.created_by?.full_name,
            created_by: replyToMessage.created_by,
          }
        : undefined

      const enqueueOutbox = async (roomId: string, lastError?: string) => {
        if (!matrixUserId || !roomId) {
          return null
        }
        const item = buildOutboxItem({
          chatId: Number(chat.id),
          roomId,
          topicId: this.activeTopic?.id ?? null,
          body: messageContent,
          userId: matrixUserId,
          clientTxnId,
          mentionUserIds,
          replyToId: resolvedReplyId,
          replySnapshot,
        })
        if (lastError) {
          item.lastError = lastError
        }
        await putOutboxItem(item)
        const optimistic = outboxItemToMessage(item, {
          id: matrixUserId,
          full_name: undefined,
        })
        if (resolvedReplyId && replyToMessage) {
          optimistic.reply_to = replyToMessage
          optimistic.reply_to_id = resolvedReplyId
          optimistic.reply_message_id = resolvedReplyId
        }
        const room = getMatrixClient()?.getRoom(roomId) ?? null
        const sdkEchoExists = Boolean(findRoomEventForClientTxn(room, clientTxnId))
        if (!sdkEchoExists) {
          this.applyRealtimeMessages(
            [...this.messages, optimistic].sort(compareMessagesAscending),
          )
          notifyViewLastMessage(chat.id, optimistic)
        }
        return optimistic
      }

      // Offline: сразу в outbox, без попытки SDK (иначе 403/сеть + потеря draft).
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        if (!roomIdHint) {
          throw new Error('Чат ещё не подключён')
        }
        const optimistic = await enqueueOutbox(roomIdHint, 'offline')
        if (!optimistic) {
          throw new Error('Не удалось поставить сообщение в очередь')
        }
        useFeedbackStore().showToast({
          type: 'info',
          message: 'Нет сети — сообщение отправится при подключении',
          time: 4000,
        })
        return optimistic
      }

      await ensureMatrixSyncAlive()
      const roomId = await this.resolveRoomIdForSend()

      const applySentMessage = (msg: IMessage): IMessage => {
        if (resolvedReplyId && replyToMessage) {
          msg.reply_to = replyToMessage
          msg.reply_to_id = resolvedReplyId
          msg.reply_message_id = resolvedReplyId
        }

        /*
         * Matrix SDK сам добавляет local echo отправленного события в room timeline,
         * а затем заменяет его remote echo после sync. Если дополнительно пушить
         * `msg` сюда, UI на секунду показывает дубль одного и того же сообщения.
         */

        if (this.currentChat) {
          const chatIndex = this.chats.findIndex(
            c => c.id === this.currentChat!.id,
          )

          if (chatIndex !== -1) {
            const updatedChat = {
              ...this.chats[chatIndex],
              last_message: msg,
              last_message_id: msg.id,
              last_matrix_event_id: msg.matrix_event_id,
            }

            this.chats.splice(chatIndex, 1, updatedChat)
          }
        }

        lastMessageDebugLog('sendMessage · update preview', {
          chatId: this.currentChat.id,
          message: summarizeLastMessageForLog(msg),
        })
        notifyViewLastMessage(this.currentChat.id, msg)
        return msg
      }

      try {
        const msg = await sendMatrixMessage(roomId, messageContent, {
          mentionUserIds,
          clientTxnId,
          inReplyToEventId: replyEventId,
        })
        return applySentMessage(msg)
      } catch (sendError) {
        if (isMatrixMembershipForbiddenError(sendError)) {
          const repaired = await ensureMatrixChatMembership({
            chatId: chat.id,
            roomId,
            chat,
            forceRepair: true,
          })
          if (repaired) {
            try {
              const msg = await sendMatrixMessage(roomId, messageContent, {
                mentionUserIds,
                clientTxnId,
                inReplyToEventId: replyEventId,
              })
              return applySentMessage(msg)
            } catch {
              // fall through to outbox
            }
          }
        }

        const optimistic = await enqueueOutbox(
          roomId || roomIdHint,
          sendError instanceof Error ? sendError.message : String(sendError),
        )
        if (optimistic) {
          useFeedbackStore().showToast({
            type: 'warning',
            message: 'Сообщение в очереди — отправим при восстановлении связи',
            time: 5000,
          })
          return optimistic
        }
        throw sendError
      }
    } catch (error) {
      debugConsoleError(error)
      logger.error('chat_sendMessage_error', {
        file: 'chatStore',
        function: 'sendMessage',
        condition: String(error),
      })
      useFeedbackStore().showToast({
        type: 'error',

        message: 'Не удалось отправить сообщение',
        time: 7000,
      })
      throw error
    } finally {
      this.isSending = false
    }
  },

  /** Подмешать pending outbox в открытый timeline (reload / reopen). */
  async hydrateOutboxMessagesForCurrentChat(): Promise<void> {
    const chat = this.currentChat
    const userId = getCurrentMatrixUserId()
    if (!chat || !userId) {
      return
    }

    const roomId = resolveActiveMessageRoomId(this.$state) || chat.matrix_room_id?.trim()
    const pending = await listOutboxForChat(userId, chat.id, roomId)
    if (!pending.length) {
      return
    }

    const navigatorOnline = typeof navigator === 'undefined' || navigator.onLine !== false
    let syncAlive = false
    if (navigatorOnline) {
      try {
        await ensureMatrixSyncAlive()
        syncAlive = true
      } catch {
        syncAlive = false
      }
    }

    if (shouldDeferOutboxUiToFlush({ navigatorOnline, syncAlive })) {
      return
    }

    const room = roomId ? getMatrixClient()?.getRoom(roomId) ?? null : null
    const optimistic = outboxItemsForUiHydrate(pending, room, { id: userId })
    if (!optimistic.length) {
      return
    }
    this.applyRealtimeMessages(
      mergeTimelineMessages(this.messages, optimistic),
    )
  },

  async flushMessageOutbox(): Promise<void> {
    await flushMatrixMessageOutbox({
      onItemSent: item => {
        if (!this.currentChat || String(this.currentChat.id) !== String(item.chatId)) {
          return
        }
        const roomId = resolveActiveMessageRoomId(this.$state)
        if (roomId && roomId !== item.roomId) {
          return
        }
        if (removeFlushedOutboxBubble(this.messages, item.id)) {
          syncViewWithCore()
        }
      },
    })
  },

  async sendMessageWithFiles(
    files: File[],
    content?: string,
    replyToId?: number | string,
    replyToMessage?: Partial<IMessage> | null,
  ): Promise<IMessage> {
    if (!checkAuth()) {
      throw new Error('Пользователь не авторизован')
    }

    if (!this.currentChat) {
      throw new Error('Нет выбранного чата')
    }

    if (!files.length) {
      throw new Error('Нет файлов для отправки')
    }

    this.isSending = true
    try {
      const chatIndex = this.chats.findIndex(c => c.id === this.currentChat!.id)
      const hasUnreadMessages = chatIndex !== -1 && (this.chats[chatIndex].unread_count || 0) > 0

      if (hasUnreadMessages) {
        this.markChatAsReadOnServer(this.currentChat.id).catch(() => {
        })
      }

      for (const file of files) {
        if (!isValidFileSize(file, VALIDATION_CONSTANTS.FILE.MAX_SIZE_BYTES)) {
          throw new Error(
            `Файл «${file.name}» превышает допустимый размер `
            + `(макс. ${VALIDATION_CONSTANTS.FILE.MAX_SIZE_MB} МБ)`,
          )
        }

        if (!isAllowedChatUploadFile(file)) {
          throw new Error(`Неподдерживаемый тип файла: ${file.name}`)
        }
      }

      const roomId = await this.resolveRoomIdForSend()

      await ensureMatrixSyncAlive()

      const replyEventId = resolveReplyMatrixEventId(replyToId, replyToMessage as IMessage | undefined)
      const resolvedReplyId = replyEventId || replyToId

      const msg = await sendMatrixMessagesWithMedia(roomId, files, content, {
        inReplyToEventId: replyEventId,
      })

      if (resolvedReplyId && replyToMessage) {
        msg.reply_to = replyToMessage as IMessage
        msg.reply_to_id = resolvedReplyId
        msg.reply_message_id = resolvedReplyId
      }

      if (
        msg.matrix_event_id
          && !this.messages.some(message => message.matrix_event_id === msg.matrix_event_id)
      ) {
        this.applyRealtimeMessages([...this.messages, msg].sort(compareMessagesAscending))
      }

      try {
        const refreshed = await Promise.race([
          fetchMatrixMessages(roomId, { loadHistory: false }),
          new Promise<IMessage[]>((_, reject) => {
            window.setTimeout(() => reject(new Error('fetchMatrixMessages timeout')), 8_000)
          }),
        ])

        let messagesToApply = refreshed

        if (
          msg.matrix_event_id
            && !refreshed.some(message => message.matrix_event_id === msg.matrix_event_id)
        ) {
          messagesToApply = [...refreshed, msg].sort(compareMessagesAscending)
        }

        if (messagesToApply.length > 0) {
          this.applyRealtimeMessages(messagesToApply)
        }
      } catch (refreshError) {
        chatDebugLog('sendMessageWithFiles · skip post-send refresh', {
          roomId,
          error: refreshError instanceof Error ? refreshError.message : String(refreshError),
        })
      }

      return msg
    } catch (error) {
      debugConsoleError('[chatStore] sendMessageWithFiles: ошибка', error)
      logger.error('chat_sendMessageWithFiles_error', {
        file: 'chatStore',
        function: 'sendMessageWithFiles',
        condition: String(error),
      })
      const message = error instanceof Error
        ? error.message.replace(/^validation:\s*/i, '')
        : 'Не удалось отправить сообщение с файлами'

      useFeedbackStore().showToast({
        type: 'error',
        message,
        time: 7000,
      })
      throw error
    } finally {
      this.isSending = false
    }
  },

  // Обновляет сообщение через Matrix
  async updateMessage(
    chatId: number,
    messageId: number | string,
    content: string,
    matrixEventIdHint?: string,
  ): Promise<IMessage> {
    if (!checkAuth()) {
      throw new Error('Пользователь не авторизован')
    }

    if (!this.currentChat) {
      throw new Error('Нет выбранного чата')
    }

    const trimmedContent = content.trim()
    if (!trimmedContent) {
      throw new Error('Текст сообщения не может быть пустым')
    }

    const messageIndex = findMessageIndex(this.messages, {
      id: messageId,
      matrixEventId: matrixEventIdHint,
    })
    if (messageIndex === -1) {
      throw new Error('Сообщение не найдено')
    }

    const existingMessage = this.messages[messageIndex]
    const matrixEventId = existingMessage.matrix_event_id || matrixEventIdHint
    const roomId = this.activeMessageRoomId
        || this.activeTopic?.matrix_room_id?.trim()
        || this.currentChat.matrix_room_id?.trim()

    if (!matrixEventId || isPendingMatrixEventId(matrixEventId) || !roomId) {
      throw new Error('Не удалось определить сообщение для редактирования')
    }

    const existingContent = existingMessage.content || existingMessage.message || ''
    const contentToSave = buildEditedMessageContent(existingContent, trimmedContent)

    const originalMessage = JSON.parse(JSON.stringify(existingMessage)) as IMessage
    const optimisticMessage: IMessage = {
      ...existingMessage,
      content: contentToSave,
      is_edited: true,
    }

    this.messages.splice(messageIndex, 1, optimisticMessage)
    syncViewWithCore()

    try {
      await editMatrixMessage(roomId, matrixEventId, contentToSave)

      const chatIndex = this.chats.findIndex(c => c.id === chatId)
      if (
        chatIndex !== -1
          && String(this.chats[chatIndex].last_message?.id) === String(messageId)
      ) {
        this.chats[chatIndex] = {
          ...this.chats[chatIndex],
          last_message: {
            ...this.chats[chatIndex].last_message!,
            content: contentToSave,
            is_edited: true,
          },
        }
      }

      useFeedbackStore().showToast({
        type: 'success',
        message: 'Сообщение изменено',
        time: 3000,
      })

      return optimisticMessage
    } catch (error) {
      this.messages.splice(messageIndex, 1, originalMessage)
      syncViewWithCore()

      logger.error('chat_updateMessage_error', {
        file: 'chatStore',
        function: 'updateMessage',
        condition: String(error),
      })
      useFeedbackStore().showToast({
        type: 'error',
        message: 'Не удалось изменить сообщение',
        time: 7000,
      })
      throw error
    }
  },

  async deleteMessage(
    chatId: number,
    messageId: number | string,
    matrixEventIdHint?: string,
  ): Promise<void> {
    if (!checkAuth()) {
      throw new Error('Пользователь не авторизован')
    }

    if (!this.currentChat) {
      throw new Error('Нет выбранного чата')
    }

    const messageIndex = findMessageIndex(this.messages, {
      id: messageId,
      matrixEventId: matrixEventIdHint,
    })
    if (messageIndex === -1) {
      throw new Error('Сообщение не найдено')
    }

    const existingMessage = this.messages[messageIndex]
    const roomId = this.activeMessageRoomId
        || this.activeTopic?.matrix_room_id?.trim()
        || this.currentChat.matrix_room_id?.trim()
    const matrixEventId = existingMessage.matrix_event_id || matrixEventIdHint

    if (!matrixEventId || isPendingMatrixEventId(matrixEventId) || !roomId) {
      throw new Error('Не удалось определить сообщение для удаления')
    }

    const originalMessage = JSON.parse(JSON.stringify(existingMessage)) as IMessage
    this.messages[messageIndex] = markMessageAsDeleted(existingMessage)
    this.syncLastMessageAfterRemoval(chatId)
    syncViewWithCore()

    try {
      await deleteMatrixMessage(roomId, matrixEventId)

      useFeedbackStore().showToast({
        type: 'success',
        message: 'Сообщение удалено',
        time: 3000,
      })
    } catch (error) {
      this.messages[messageIndex] = originalMessage
      this.syncLastMessageAfterRemoval(chatId)
      syncViewWithCore()

      logger.error('chat_deleteMessage_error', {
        file: 'chatStore',
        function: 'deleteMessage',
        condition: String(error),
      })
      useFeedbackStore().showToast({
        type: 'error',
        message: 'Не удалось удалить сообщение',
        time: 7000,
      })
      throw error
    }
  },

  syncLastMessageAfterRemoval(chatId: number): void {
    const chatIndex = this.chats.findIndex(c => c.id === chatId)
    if (chatIndex === -1) {
      return
    }

    const lastMessage = this.messages.length > 0
      ? this.messages[this.messages.length - 1]
      : undefined

    const updatedChats = [...this.chats]
    updatedChats[chatIndex] = {
      ...updatedChats[chatIndex],
      last_message: lastMessage,
      last_message_id: lastMessage?.id,
      last_matrix_event_id: lastMessage?.matrix_event_id,
    }
    this.chats = updatedChats
  },

  async fetchMessageReaders(
    chatId: number,
    messageId: number | string,
    matrixEventIdHint?: string,
  ): Promise<any[]> {
    if (!checkAuth()) {
      return []
    }

    const message = findMessage(this.messages, {
      id: messageId,
      matrixEventId: matrixEventIdHint,
    })
      ?? (this.currentChat?.last_message
        && (
          String(this.currentChat.last_message.id) === String(messageId)
          || (matrixEventIdHint
            && this.currentChat.last_message.matrix_event_id === matrixEventIdHint)
        )
        ? this.currentChat.last_message
        : undefined)
    const matrixEventId = message?.matrix_event_id || matrixEventIdHint

    if (!matrixEventId) {
      return []
    }

    const chat = this.chats.find(item => item.id === chatId) ?? this.currentChat
    const roomId = chat?.matrix_room_id ?? await resolveMatrixRoomIdByChatId(chatId)

    if (!roomId) {
      return []
    }

    try {
      const matrixClient = await startMatrixClient()
      const room = matrixClient.getRoom(roomId)
      if (!room) {
        return []
      }

      return await getMatrixMessageReaders(matrixClient, room, matrixEventId)
    } catch (error) {
      logger.error('chat_fetchMessageReaders_error', {
        file: 'chatStore',
        function: 'fetchMessageReaders',
        condition: String(error),
      })

      return []
    }
  },

  async uploadAttachment(messageId: number, file: File): Promise<void> {
    if (!checkAuth()) {
      throw new Error('Пользователь не авторизован')
    }

    if (!this.currentChat) {
      throw new Error('Нет выбранного чата')
    }

    try {
      const roomId = this.activeMessageRoomId
          ?? await resolveMatrixRoomIdByChatId(this.currentChat.id)

      if (!roomId) {
        throw new Error('Chat room not found')
      }

      await sendMatrixMessagesWithMedia(roomId, [file])

      await this.fetchMessages(this.currentChat.id)

      useFeedbackStore().showToast({
        type: 'success',
        message: 'Файл успешно загружен',
        time: 3000,
      })
    } catch (error) {
      logger.error('chat_uploadAttachment_error', {
        file: 'chatStore',
        function: 'uploadAttachment',
        condition: String(error),
      })
      useFeedbackStore().showToast({
        type: 'error',

        message: 'Не удалось загрузить файл',
        time: 7000,
      })
      throw error
    }
  },

  async forwardMessage(input: {
    sourceEventId: string
    destinationChatId: number
    destinationTopicId?: number | null
    attribution?: 'none' | 'author'
    silent?: boolean
  }): Promise<void> {
    if (!checkAuth()) {
      throw new Error('Пользователь не авторизован')
    }

    const sourceRoomId = this.activeMessageRoomId
      || this.currentChat?.matrix_room_id?.trim()
      || ''
    if (!sourceRoomId) {
      throw new Error('Исходный чат ещё не подключён')
    }
    if (!input.sourceEventId) {
      throw new Error('Не указано сообщение для пересылки')
    }

    const destinationChat = this.chats.find(chat => chat.id === input.destinationChatId)
      || (this.currentChat?.id === input.destinationChatId ? this.currentChat : null)
    if (!destinationChat) {
      throw new Error('Чат назначения не найден')
    }

    let prepared: Awaited<ReturnType<typeof ChatApiService.prepareForward>> | null = null

    try {
      prepared = await ChatApiService.prepareForward({
        source_room_id: sourceRoomId,
        source_event_id: input.sourceEventId,
        destination_chat_id: input.destinationChatId,
        destination_topic_id: input.destinationTopicId ?? undefined,
        attribution: input.attribution ?? 'author',
      })

      await ensureMatrixSyncAlive()
      await ensureMatrixChatMembership({
        chatId: destinationChat.id,
        roomId: prepared.destination_room_id,
        chat: destinationChat,
      })

      const { eventId } = await sendMatrixForwardedMessage(prepared.destination_room_id, {
        forwardId: prepared.forward_id,
        sourceRoomId,
        sourceEventId: input.sourceEventId,
        snapshot: prepared.snapshot,
      })

      await ChatApiService.completeForward({
        forward_id: prepared.forward_id,
        destination_event_id: eventId,
      })

      if (!input.silent) {
        useFeedbackStore().showToast({
          type: 'success',
          message: 'Сообщение переслано',
          time: 3000,
        })
      }
    } catch (error) {
      if (prepared?.forward_id) {
        try {
          await ChatApiService.completeForward({
            forward_id: prepared.forward_id,
            error: error instanceof Error ? error.message : String(error),
          })
        } catch {
        }
      }

      logger.error('chat_forwardMessage_error', {
        file: 'chatStore',
        function: 'forwardMessage',
        condition: String(error),
      })
      throw error
    }
  },

  async forwardMessages(input: {
    sourceEventIds: string[]
    destinationChatId: number
    destinationTopicId?: number | null
    attribution?: 'none' | 'author'
  }): Promise<void> {
    const eventIds = input.sourceEventIds.filter(Boolean)
    if (!eventIds.length) {
      throw new Error('Не указаны сообщения для пересылки')
    }

    if (eventIds.length === 1) {
      await this.forwardMessage({
        sourceEventId: eventIds[0],
        destinationChatId: input.destinationChatId,
        destinationTopicId: input.destinationTopicId,
        attribution: input.attribution,
      })
      return
    }

    for (const sourceEventId of eventIds) {
      await this.forwardMessage({
        sourceEventId,
        destinationChatId: input.destinationChatId,
        destinationTopicId: input.destinationTopicId,
        attribution: input.attribution,
        silent: true,
      })
    }

    useFeedbackStore().showToast({
      type: 'success',
      message: `Переслано сообщений: ${eventIds.length}`,
      time: 3000,
    })
  },
})
